import React, { useEffect, useState } from "react";
import { FaCheck, FaEye, FaMapMarkerAlt, FaClock } from "react-icons/fa";
import { AI_RECOMMENDATIONS, STAGES, stageIndex, stageOf } from "./aiHireStages";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-20" -> "20 Oct". */
export function fmtDate(iso) {
  if (!iso) return "--";
  const [, m, d] = iso.split("-");
  return `${Number(d)} ${MONTHS[Number(m) - 1]}`;
}

/** Lakhs per year: 11.5 -> "11.5 L". */
export const fmtCtc = (lakhs) => (lakhs == null ? "--" : `${lakhs} L`);

export const fmtHour = (h) => `${String(h).padStart(2, "0")}:00`;

/** Loads preview data through `aiHireApi` (async, so real endpoints can replace it later). */
export function useAiHireData(load, deps) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    load().then((d) => live && setData(d));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return data;
}

export function PreviewBanner() {
  return (
    <div className="aihire-banner" role="note">
      <FaEye aria-hidden="true" />
      <span>
        <strong>Preview only.</strong> Sample data showing the planned Ai Hire flow. Nothing is saved.
      </span>
    </div>
  );
}

/** Looks like a normal action but does nothing in the preview. */
export function PreviewButton({ children, icon: Icon, primary = false, small = false }) {
  return (
    <button
      type="button"
      className={`aihire-btn${primary ? " aihire-btn--primary" : ""}${small ? " aihire-btn--sm" : ""}`}
      disabled
      title="Preview only"
    >
      {Icon ? <Icon aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

export function StageChip({ stage }) {
  const s = stageOf(stage);
  return <span className={`aihire-chip aihire-tone--${s.tone}`}>{s.label}</span>;
}

export function Tone({ tone, children }) {
  return <span className={`aihire-chip aihire-tone--${tone}`}>{children}</span>;
}

export function AiScoreBadge({ score, recommendation }) {
  if (score == null) return <span className="aihire-score aihire-score--none">Pending</span>;
  const tone = AI_RECOMMENDATIONS[recommendation] || "gray";
  return (
    <span className={`aihire-score aihire-tone--${tone}`} title="Ai Hire score out of 100">
      <b>{score}</b>
      {recommendation ? <span>{recommendation}</span> : null}
    </span>
  );
}

export function MatchTags({ tags }) {
  return (
    <span className="aihire-match">
      {tags.map((t) => (
        <span key={t} className="aihire-match__tag">
          {t.startsWith("Location") ? <FaMapMarkerAlt aria-hidden="true" /> : <FaClock aria-hidden="true" />}
          {t}
        </span>
      ))}
    </span>
  );
}

/** Main-path progress; exit stages (Rejected, Declined...) are shown as a chip instead. */
export function StageStepper({ stage }) {
  const current = stageIndex(stage);
  if (current < 0) {
    return (
      <div className="aihire-stepper aihire-stepper--exit">
        <StageChip stage={stage} />
      </div>
    );
  }
  return (
    <ol className="aihire-stepper">
      {STAGES.map((s, i) => {
        const state = i < current ? "done" : i === current ? "current" : "todo";
        return (
          <li key={s.id} className={`aihire-step aihire-step--${state}`} title={`${s.label} (moved by ${s.owner})`}>
            <span className="aihire-step__dot">{state === "done" ? <FaCheck aria-hidden="true" /> : i + 1}</span>
            <span className="aihire-step__label">{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function SummaryStrip({ items }) {
  return (
    <div className="aihire-summary">
      {items.map((it) => (
        <div key={it.label} className="aihire-summary__item">
          <b>{it.value}</b>
          <span>{it.label}</span>
        </div>
      ))}
    </div>
  );
}

/** Segmented chips; `options` = [{ id, label, count? }]. */
export function ChipGroup({ options, value, onChange, label }) {
  return (
    <div className="aihire-chips" role="group" aria-label={label}>
      {label ? <span className="aihire-chips__label">{label}</span> : null}
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          className={`aihire-chipbtn${value === o.id ? " is-on" : ""}`}
          aria-pressed={value === o.id}
          onClick={() => onChange(o.id)}
        >
          {o.label}
          {o.count != null ? <em>{o.count}</em> : null}
        </button>
      ))}
    </div>
  );
}

/** Small labelled select; `options` = strings or { value, label }. First option is "All". */
export function FilterSelect({ label, value, onChange, options, allLabel = "All" }) {
  return (
    <label className="aihire-filter">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{allLabel}</option>
        {options.map((o) => {
          const opt = typeof o === "string" ? { value: o, label: o } : o;
          return (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          );
        })}
      </select>
    </label>
  );
}

export function Toolbar({ children }) {
  return <div className="aihire-toolbar">{children}</div>;
}

export function EmptyRow({ colSpan, children }) {
  return (
    <tr>
      <td colSpan={colSpan} className="aihire-empty">
        {children}
      </td>
    </tr>
  );
}
